class DropdownMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'show-from-bottom',
    };
    this.selectors = {
      header: '.shopify-section-header',
      background: '.header-background',
      topMenuItem: '.header__menu-item--top',
      menus: 'dropdown-menu, mega-menu',
      wrapper: '.list-menu',
      childAnimate: '.list-menu-dropdown > .list-menu'
    };
    this.timeout;

    this.addEventListener('mouseenter', this.handleMouseover.bind(this));

    // keyboard support ──
    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  connectedCallback() {
    this.setupAria();
  }

  // set initial ARIA state on the trigger + submenu ──
  setupAria() {
    const trigger = this.querySelector(':scope > a.header__menu-item, :scope > button.header__menu-item');
    const submenu = this.querySelector(':scope > .list-menu-dropdown');

    if (!trigger || !submenu) return;

    this._trigger = trigger;
    this._submenu = submenu;

    // Only set ARIA on items that actually have a dropdown
    if (!submenu.querySelector('.list-menu')) return;

    // Give the submenu a unique ID for aria-controls
    if (!submenu.id) {
      submenu.id = 'DropdownSubmenu-' + Math.random().toString(36).slice(2, 9);
    }

    trigger.setAttribute('aria-haspopup', 'true');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', submenu.id);
  }

  // sync aria-expanded whenever is-active toggles ──
  syncAria() {
    if (!this._trigger) return;
    const isOpen = this.classList.contains(this.classes.active);
    this._trigger.setAttribute('aria-expanded', String(isOpen));
  }

  // keyboard handler ──
  handleKeydown(e) {
    const trigger = this._trigger;
    const submenu = this._submenu;
    if (!trigger || !submenu) return;

    const isTopLevel = !!this.querySelector(this.selectors.topMenuItem);
    const isOpen = this.classList.contains(this.classes.active);

    // Only handle keys when focus is on the trigger or inside this menu
    switch (e.key) {
      case 'Enter':
      case ' ':
        // Only intercept if this trigger has children — otherwise let the link navigate
        if (trigger === document.activeElement && submenu.querySelector('.list-menu')) {
          e.preventDefault();
          if (isOpen) {
            this.closeKeyboard();
          } else {
            this.openKeyboard();
          }
        }
        break;

      case 'ArrowDown':
        if (trigger === document.activeElement && submenu.querySelector('.list-menu')) {
          e.preventDefault();
          this.openKeyboard();
          // Move focus into first submenu link
          const firstLink = submenu.querySelector('a, button');
          if (firstLink) firstLink.focus();
        } else if (isOpen) {
          // Arrow within the submenu
          e.preventDefault();
          this.moveFocus(submenu, 1);
        }
        break;

      case 'ArrowUp':
        if (isOpen) {
          e.preventDefault();
          this.moveFocus(submenu, -1);
        }
        break;

      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation(); // Don't let parent menus also close
          this.closeKeyboard();
          trigger.focus();
        }
        break;

      case 'ArrowRight':
        // Open child submenu if this item has one
        if (isOpen && !isTopLevel) {
          const focused = document.activeElement;
          const parentDropdown = focused?.closest('dropdown-menu');
          if (parentDropdown) {
            const childSubmenu = parentDropdown.querySelector(':scope > .list-menu-dropdown');
            if (childSubmenu) {
              e.preventDefault();
              parentDropdown.openKeyboard();
              const firstChild = childSubmenu.querySelector('a, button');
              if (firstChild) firstChild.focus();
            }
          }
        }
        break;

      case 'ArrowLeft':
        // Close back to parent
        if (!isTopLevel) {
          e.preventDefault();
          this.closeKeyboard();
          trigger.focus();
        }
        break;
    }
  }

  // open via keyboard (reuses existing visual logic) ──
  openKeyboard() {
    this.bindEvent();
    this.removeClasses();
    this.header = this.header || document.querySelector(this.selectors.header);
    this.header.classList.add(this.classes.hover);
    this.classList.add(this.classes.active);

    // Animate child
    const childAnimate = this.querySelector(this.selectors.childAnimate);
    if (childAnimate) {
      setTimeout(() => {
        childAnimate.classList.add(this.classes.animate);
      }, 150);
    }

    this.syncAria();
  }

  // close via keyboard ──
  closeKeyboard() {
    this.reset();
    this.resetBackground();
    if (this.header) {
      this.header.classList.remove(this.classes.hover);
    }
    this.syncAria();
  }

  // arrow-key focus movement within submenu ──
  moveFocus(container, direction) {
    const focusables = Array.from(
      container.querySelectorAll('a:not([tabindex="-1"]), button:not([tabindex="-1"])')
    );
    if (!focusables.length) return;

    const current = focusables.indexOf(document.activeElement);
    let next = current + direction;
    if (next < 0) next = focusables.length - 1;
    if (next >= focusables.length) next = 0;
    focusables[next].focus();
  }

  handleMouseover() { 
    if (this.classList.contains(this.classes.active)) return;

    this.bindEvent();
    this.removeClasses();
    this.addClasses();
    this.resetBackground();
    this.animateMenu();

    this.syncAria();
  }

  handleMouseleave() {
    if (!this.header) return;

    this.reset();
    this.resetBackground();

    this.header.classList.remove(this.classes.hover);
    this.wrapper.removeEventListener('mouseleave', this.onHeaderMouseLeaveEvent);

    this.syncAria();
  }

  bindEvent() {
    this.onHeaderMouseLeaveEvent = this.onHeaderMouseLeaveEvent || this.handleMouseleave.bind(this);

    this.header = this.header || document.querySelector(this.selectors.header);

    this.wrapper = this.closest(this.selectors.wrapper)
    this.wrapper.addEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
  }

  addClasses() {
    const isTopLevel = this.querySelector(this.selectors.topMenuItem);

    let delay = 0;
    if (isTopLevel && !this.isHover()) {
      delay = 150;

      if (this.closest('.header--top-center') || this.closest('.header--top-left')) {
        delay = 200;
      }
    }
    this.timeout = setTimeout(() => {
      if (this.isHover()) {
        this.classList.add(this.classes.active);
        this.syncAria();
      }
    }, delay);

    this.header.classList.add(this.classes.hover);
  }

  removeClasses() {
    const menus = this.header.querySelectorAll(this.selectors.menus);

    menus.forEach((menu) => {
      let found = false;
      const childMenus = menu.querySelectorAll('dropdown-menu');
      childMenus.forEach((element) => {
        if (element === this) {
          found = true;
          return;
        }
      });

      if (!found) {
        menu.reset();
        menu.syncAria?.();
      }
    });
  }

  isHover() {
    return this.header.classList.contains(this.classes.hover);
  }

  reset() {
    this.classList.remove(this.classes.active);

    const childAnimate =  this.querySelector(this.selectors.childAnimate);
    if (childAnimate) childAnimate.classList.remove(this.classes.animate);

    clearTimeout(this.timeout);
  }

  resetBackground() {
    const background = this.header.querySelector(this.selectors.background);
    background.classList.remove(this.classes.active);
  }

  animateMenu() {
    const childAnimate =  this.querySelector(this.selectors.childAnimate);
    if (childAnimate) {
      setTimeout(() => {
        childAnimate.classList.add(this.classes.animate);
      }, 150);
    }
  }

  close() {
    this.handleMouseleave();
  }
}
customElements.define('dropdown-menu', DropdownMenu);


class MegaMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'show-from-bottom',
    };
    this.selectors = {
      header: '.shopify-section-header',
      background: '.header-background',
      dropdown: '.list-menu-dropdown',
      menus: 'dropdown-menu, mega-menu',
      wrapper: '.list-menu',
      childAnimate: '.mega-menu__item, .mega-menu__promo'
    };
    this.timeout = [];

    this.addEventListener('mouseenter', this.handleMouseover.bind(this));

    // keyboard support ──
    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  connectedCallback() {
    this.setupAria();
  }

  // set initial ARIA state ──
  setupAria() {
    const trigger = this.querySelector(':scope > a.header__menu-item, :scope > button.header__menu-item');
    const submenu = this.querySelector(':scope > .list-menu-dropdown');

    if (!trigger) return;

    this._trigger = trigger;
    this._submenu = submenu;

    // Only set ARIA if there's actually a dropdown
    if (!submenu) return;

    if (!submenu.id) {
      submenu.id = 'MegaSubmenu-' + Math.random().toString(36).slice(2, 9);
    }

    trigger.setAttribute('aria-haspopup', 'true');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', submenu.id);
    trigger.tagName==="A"&&trigger.setAttribute("role","button")
  }

  syncAria() {
    if (!this._trigger) return;
    const isOpen = this.classList.contains(this.classes.active);
    this._trigger.setAttribute('aria-expanded', String(isOpen));
  }

  // keyboard handler ──
  handleKeydown(e) {
    const trigger = this._trigger;
    const submenu = this._submenu;
    if (!trigger) return;

    const isOpen = this.classList.contains(this.classes.active);

    switch (e.key) {
      case 'Enter':
      case ' ':
        if (trigger === document.activeElement && submenu) {
          e.preventDefault();
          if (isOpen) {
            this.closeKeyboard();
          } else {
            this.openKeyboard();
          }
        }
        break;

      case 'ArrowDown':
        if (trigger === document.activeElement && submenu) {
          e.preventDefault();
          this.openKeyboard();
          const firstLink = submenu.querySelector('a, button');
          if (firstLink) firstLink.focus();
        } else if (isOpen && submenu) {
          e.preventDefault();
          this.moveFocus(submenu, 1);
        }
        break;

      case 'ArrowUp':
        if (isOpen && submenu) {
          e.preventDefault();
          this.moveFocus(submenu, -1);
        }
        break;

      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation();
          this.closeKeyboard();
          trigger.focus();
        }
        break;
    }
  }

  openKeyboard() {
    this.bindEvent();
    this.removeClasses();
    this.addClasses();
    this.showSublist();
    this.syncAria();
  }

  closeKeyboard() {
    this.reset();
    this.resetBackground();
    if (this.header) {
      this.header.classList.remove(this.classes.hover);
    }
    this.syncAria();
  }

  moveFocus(container, direction) {
    const focusables = Array.from(
      container.querySelectorAll('a:not([tabindex="-1"]), button:not([tabindex="-1"])')
    );
    if (!focusables.length) return;

    const current = focusables.indexOf(document.activeElement);
    let next = current + direction;
    if (next < 0) next = focusables.length - 1;
    if (next >= focusables.length) next = 0;
    focusables[next].focus();
  }

  handleMouseover() {
    if (this.classList.contains(this.classes.active)) return;
    
    this.bindEvent();
    this.removeClasses();
    this.addClasses();
    this.showSublist();

    this.syncAria();
  }

  handleMouseleave() {
    if (!this.header) return;

    this.reset();
    this.resetBackground();

    this.header.classList.remove(this.classes.hover);
    this.wrapper.removeEventListener('mouseleave', this.onHeaderMouseLeaveEvent);

    this.syncAria();
  }

  handleTouchMoveOutside(ev) {
    if (!this.header) return;
    
    let target_section = ev.targetTouches[0].target.closest(this.selectors.header);
    if(!target_section || this.header.classList.contains('shopify-section-header-hidden')) {
      this.reset();
      this.resetBackground();
      
      this.header.classList.remove(this.classes.hover);
      document.removeEventListener('touchmove', this.onHeaderTouchMoveOutsideEvent);

      this.syncAria();
    }
  }

  bindEvent() {
    this.onHeaderMouseLeaveEvent = this.onHeaderMouseLeaveEvent || this.handleMouseleave.bind(this);
    this.onHeaderTouchMoveOutsideEvent = this.onHeaderTouchMoveOutsideEvent || this.handleTouchMoveOutside.bind(this);
    
    this.header = this.header || document.querySelector(this.selectors.header);

    this.wrapper = this.closest(this.selectors.wrapper)
    this.wrapper.addEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
    document.addEventListener('touchmove', this.onHeaderTouchMoveOutsideEvent);
  }

  showSublist() { 
    if(this.querySelector(".list-menu-dropdown")){   
      const dropdown = this.querySelector(this.selectors.dropdown);
      const background = this.header.querySelector(this.selectors.background);
      let headerHeight = this.header.clientHeight;
      let dropDownHeight = dropdown.clientHeight;
      background.style.setProperty('--height', headerHeight + dropDownHeight + 'px');
      background.classList.add(this.classes.active);
    }else{ 
      this.resetBackground();
    }
  }

  addClasses() {
    this.classList.add(this.classes.active);

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    const delay = childAnimate.length > 5 ? 75 : 150;
    childAnimate.forEach((element, index) => {
      const timeout = setTimeout(() => {
        element.classList.add(this.classes.animate);
      }, (delay * index) + 100);
      
      this.timeout.push(timeout);
    });
    
    this.header.classList.add(this.classes.hover);
  }

  removeClasses() {
    const menus = this.header.querySelectorAll(this.selectors.menus);
    menus.forEach((menu) => {
      menu.reset();
      menu.syncAria?.();
    });
  }

  reset() {
    this.classList.remove(this.classes.active);

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    childAnimate.forEach((element) => {
      element.classList.remove(this.classes.animate);
    });
    this.timeout.forEach((timeout) => {
      clearTimeout(timeout);
    });
  }

  resetBackground() {
    const background = this.header.querySelector(this.selectors.background);
    background.classList.remove(this.classes.active);
  }

  close() {
    this.handleMouseleave();
  }
}
customElements.define('mega-menu', MegaMenu);


// close all menus when focus leaves the entire nav
document.addEventListener('focusin', (e) => {
  const header = document.querySelector('.shopify-section-header');
  if (!header) return;

  // If focus moved outside the header, close everything
  if (!header.contains(e.target)) {
    const openMenus = header.querySelectorAll('dropdown-menu.is-active, mega-menu.is-active');
    openMenus.forEach((menu) => {
      menu.reset();
      menu.syncAria?.();
    });
    const background = header.querySelector('.header-background');
    if (background) background.classList.remove('is-active');
    header.classList.remove('is-hover');
  }
});